import { useCallback, useState, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import type { PageKey } from '@/lib/permissions';
import { useMyPermissions } from '@/hooks/useMyPermissions';
import {
  LayoutDashboard,
  FolderKanban,
  Users,
  FolderArchive,

  Wallet,
  ShieldCheck,
  ClipboardSignature,
  FileSignature,
  GraduationCap,
  MessageCircleQuestion,
  LogOut,
  Menu,
  X,
  ChevronDown,
  UserPlus,
  UserCheck,
  Building2,
  UsersRound,
  ScrollText,
  ShieldOff,
  CalendarDays,
  Database,
  Search,
  Tag,
  FileText,
  FolderTree,
  ClipboardEdit,
  PanelLeftOpen,
  PanelLeftClose,
  UserCircle,
  KeyRound,
  Smartphone,
  Library,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useIdleTimeout } from '@/hooks/useIdleTimeout';
import { SiteSwitcher } from '@/components/layout/SiteSwitcher';
import { InviteModal } from '@/components/layout/InviteModal';
import { TasksBell } from '@/components/layout/TasksBell';
import { ChangePasswordDialog } from '@/pages/profile/ChangePasswordDialog';
import { ResetAuthenticatorDialog } from '@/pages/profile/ResetAuthenticatorDialog';
import { cn } from '@/lib/cn';
import { humanRole } from '@/lib/humanise';

const CAN_INVITE = new Set(['platform_admin', 'application_admin', 'site_admin']);
type NavItem = { to: string; label: string; icon: typeof FileText; page: PageKey };
type NavGroup = { label: string; items: NavItem[] };

/**
 * Phase 4 (1 Oct 2026) — every entry names the page it opens; the sidebar
 * shows an entry only when lib/permissions.ts canSeePage() allows the user's
 * role, the same check the route guard makes. A group with no visible entries
 * is dropped. The server still decides which studies each page lists.
 */
const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Overview',
    items: [{ to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, page: 'dashboard' }],
  },
  {
    label: 'Clinical',
    items: [
      { to: '/studies', label: 'Studies', icon: FolderKanban, page: 'studies' },
      { to: '/subjects', label: 'Subjects', icon: Users, page: 'subjects' },
      { to: '/visits', label: 'Visit Calendar', icon: CalendarDays, page: 'visits' },
      // Phase 7A — designers see the Form Library; data-entry roles get an
      // eSource shortcut that resolves via the /esource redirect
      // (routes.tsx::EsourceLandingRedirect) to the subjects list.
      { to: '/forms', label: 'Forms', icon: FileText, page: 'forms' },
      { to: '/esource', label: 'eSource', icon: ClipboardEdit, page: 'esource' },
      { to: '/queries', label: 'Queries', icon: MessageCircleQuestion, page: 'queries' },
    ],
  },
  // Sprint-18 Item #1 — eISF + everything that operates on documents
  // groups under "Documentation" rather than living in "Clinical."
  {
    label: 'Documentation',
    items: [
      { to: '/binder', label: 'eISF Binder', icon: FolderArchive, page: 'binder' },
      { to: '/approvals', label: 'Approval Needed', icon: ClipboardSignature, page: 'approvals' },
      { to: '/delegations', label: 'Delegations', icon: ClipboardSignature, page: 'delegations' },
      { to: '/search', label: 'Search', icon: Search, page: 'search' },
      { to: '/signature-queue', label: 'eSignature Queue', icon: FileSignature, page: 'signature-queue' },
      // Client feedback 2026-08-04 — site-wide shared document library,
      // separate from the study-scoped eISF binder above.
      { to: '/document-library', label: 'Document Library', icon: Library, page: 'document-library' },
    ],
  },
  {
    label: 'Administration',
    items: [
      { to: '/team', label: 'Site Team', icon: UserPlus, page: 'team' },
      { to: '/training', label: 'Training', icon: GraduationCap, page: 'training' },
      { to: '/finance', label: 'Finance', icon: Wallet, page: 'finance' },
      { to: '/compliance', label: 'Compliance', icon: ShieldCheck, page: 'compliance' },
      // The one place monitors/auditors are put on a study (13 Oct 2026);
      // shown to whoever holds compliance.monitor_assignments in the RBAC grid.
      { to: '/admin/monitors', label: 'Monitor Assignments', icon: UserCheck, page: 'monitor-assignments' },
    ],
  },
  // DREAM-8: Platform Admin section.
  {
    label: 'Platform Admin',
    items: [
      { to: '/admin/sites', label: 'Sites', icon: Building2, page: 'platform-admin' },
      { to: '/admin/users', label: 'Users', icon: UsersRound, page: 'platform-admin' },
      { to: '/admin/blinding', label: 'Blinding', icon: ShieldOff, page: 'blinding' },
      { to: '/admin/audit', label: 'Platform Audit Log', icon: ScrollText, page: 'platform-admin' },
      // Role x Module access grid, at CRUD granularity. Configures intent and
      // exports it; enforcement still lives in lib/permissions.ts and the
      // back-end guards. See lib/rbacModel.ts.
      { to: '/admin/rbac', label: 'RBAC Configuration', icon: ShieldCheck, page: 'rbac' },
    ],
  },
  // Master data — Platform Admin only (Harish, 1 Oct 2026). Field-template
  // CRUD lives inside the Form Designer palette; /admin/field-templates stays
  // reachable by URL.
  {
    label: 'Master Data',
    items: [
      { to: '/admin/eisf-master-data', label: 'eISF Catalog', icon: Database, page: 'build-master-data' },
      { to: '/admin/doa-responsibilities', label: 'DoA Responsibilities', icon: ClipboardSignature, page: 'build-master-data' },
      // Sprint-18 — regulatory tag vocabulary used by document tagging + search.
      { to: '/admin/regulatory-tags', label: 'Regulatory Tags', icon: Tag, page: 'regulatory-tags' },
      // Sprint-19 — sponsor master list feeding the Study Onboarding wizard dropdown.
      { to: '/admin/sponsors', label: 'Sponsors', icon: Building2, page: 'sponsors' },
      // F8 - reusable ISF folder layouts picked at study creation.
      { to: '/admin/binder-templates', label: 'Binder Templates', icon: FolderTree, page: 'build-master-data' },
    ],
  },
];

export function AppShell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [changePwdOpen, setChangePwdOpen] = useState(false);
  const [resetMfaOpen, setResetMfaOpen] = useState(false);
  // Nav visibility: the RBAC grid where a module is registered, the legacy
  // table everywhere else. See hooks/useMyPermissions.ts.
  const perms = useMyPermissions();
  // team.invite is a single requireOperation on the backend, so the RBAC
  // grid is authoritative here; CAN_INVITE is only the pre-load fallback.
  const canInvite = perms.holdsOperation('team.invite', [...CAN_INVITE]);

  // Controls whether the full left sidebar is visible (desktop toggle).
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // 15-min idle auto-logout when signed in.
  const onIdle = useCallback(async () => {
    await logout('idle');
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  useIdleTimeout(onIdle, { enabled: !!user });

  // Role-based avatar — ownership is expressed at role level, not personal initials.
  const roleAbbrev = (user?.role || '')
    .replace(/_/g, ' ')
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const avatar = roleAbbrev || 'DT';

  return (
    <div className="min-h-screen bg-app flex flex-col">
      {/* ── Topbar (60px navy) ────────────────────────────── */}
      {/* data-print-hide: app chrome is screen-only. Printable views render
          their own branded <PrintHeader/> instead (globals.css @media print). */}
      <header
        data-print-hide
        className="h-[60px] flex items-center justify-between px-4 md:px-6 bg-header border-b border-navy-700 sticky top-0 z-30"
      >
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="md:hidden text-text-on-navy-2 hover:text-white p-1"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={18} />
          </button>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-3 group"
          >
            <div className="w-9 h-9 rounded-sm bg-gradient-to-br from-blue-400 to-navy grid place-items-center text-white text-[12px] font-extrabold tracking-tight">
              DT
            </div>
            <div className="text-left hidden sm:block">
              <div className="text-[15px] font-bold text-white leading-tight tracking-tight">
                DreamTrials
              </div>
              <div className="text-[10px] text-text-on-navy-3 leading-tight uppercase tracking-wider">
                Clinical Trial Management
              </div>
            </div>
          </button>

        </div>

        <div className="flex items-center gap-3">
          {user && user.allowedSites && user.allowedSites.length > 1 ? (
            <div className="hidden md:block">
              <SiteSwitcher />
            </div>
          ) : (
            user?.activeSiteId && (
              <div className="hidden md:flex items-center gap-2 px-3 py-1 rounded-sm bg-navy-700/60 border border-navy-700">
                <span className="dot dot-positive" />
                <span className="text-[10.5px] uppercase tracking-wider text-text-on-navy-3">
                  Site
                </span>
                <span className="text-[12px] font-semibold text-white">
                  {(user.siteNames?.[user.activeSiteId]) || user.activeSiteId.slice(0, 8)}
                </span>
              </div>
            )
          )}

          {user && canInvite && (
            <button
              type="button"
              onClick={() => setInviteOpen(true)}
              className="hero-btn hidden md:inline-flex"
            >
              <UserPlus size={13} />
              Invite
            </button>
          )}

          <TasksBell />

          <div className="relative">
            <button
              type="button"
              onClick={() => setUserMenuOpen((v) => !v)}
              className="flex items-center gap-2.5 pl-1 pr-2 py-1 rounded-sm hover:bg-navy-700 transition-colors"
            >
              <span className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-navy grid place-items-center text-white text-[11px] font-bold ring-2 ring-navy-700">
                {avatar}
              </span>
              <span className="hidden md:block text-left leading-tight">
                <span className="block text-[12px] font-semibold text-white">
                  {user?.role ? humanRole(user.role) : 'Signed in'}
                </span>
                <span className="block text-[10px] text-text-on-navy-3 uppercase tracking-wider">
                  DreamTrials session
                </span>
              </span>
              <ChevronDown size={14} className="hidden md:block text-text-on-navy-3" />
            </button>

            {userMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  aria-hidden
                  onClick={() => setUserMenuOpen(false)}
                />
                <div className="absolute right-0 top-[calc(100%+6px)] z-20 w-64 overflow-hidden rounded-sm border border-border bg-white shadow-card-3">
                  <div className="border-b border-border-soft px-4 py-3">
                    <div className="truncate text-sm font-bold text-text-1">
                      {user?.fullName ?? 'Clinician'}
                    </div>
                    <div className="truncate text-xs text-text-3">{user?.email}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setUserMenuOpen(false);
                      navigate('/me/profile');
                    }}
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-text-2 hover:bg-soft"
                  >
                    <UserCircle size={14} />
                    Your profile
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setUserMenuOpen(false);
                      setChangePwdOpen(true);
                    }}
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-text-2 hover:bg-soft"
                  >
                    <KeyRound size={14} />
                    Change password
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setUserMenuOpen(false);
                      setResetMfaOpen(true);
                    }}
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-text-2 hover:bg-soft"
                  >
                    <Smartphone size={14} />
                    Reset authenticator
                  </button>
                  <div className="border-t border-border-soft" />
                  <button
                    onClick={async () => {
                      setUserMenuOpen(false);
                      await logout('manual');
                      navigate('/login', { replace: true });
                    }}
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-text-2 hover:bg-soft"
                  >
                    <LogOut size={14} />
                    Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ── Body: sidebar + main ───────────────────────────── */}
      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <aside
          data-print-hide
          className={cn(
            'fixed inset-y-[60px] left-0 z-40 flex flex-col border-r border-border bg-surface transition-all duration-200 overflow-hidden',
            'md:sticky md:top-[60px] md:h-[calc(100vh-60px)] md:translate-x-0',
            sidebarOpen ? 'md:w-[230px] md:shrink-0' : 'md:w-0 md:border-r-0',
            mobileNavOpen ? 'w-[230px] translate-x-0' : 'w-[230px] -translate-x-full',
          )}
        >
          <div className="flex items-center justify-between px-4 py-3 md:hidden border-b border-border-soft">
            <span className="eyebrow">Navigation</span>
            <button
              type="button"
              onClick={() => setMobileNavOpen(false)}
              aria-label="Close menu"
              className="text-text-3"
            >
              <X size={16} />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto py-4">
            {NAV_GROUPS
              .map((g) => ({ label: g.label, items: user ? g.items.filter((it) => perms.isVisible(it.page)) : [] }))
              .filter((g) => g.items.length > 0)
              .map((g) => (
              <div key={g.label} className="mb-1 px-3">
                <div className="eyebrow px-3 py-1.5 mb-0.5">
                  {g.label}
                </div>
                {g.items.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    onClick={() => setMobileNavOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'group relative flex items-center gap-3 px-3 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-navy-50 text-navy font-semibold'
                          : 'text-text-2 hover:bg-soft hover:text-text-1',
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-navy" />
                        )}
                        <it.icon
                          size={15}
                          className={
                            isActive
                              ? 'text-navy'
                              : 'text-text-3 group-hover:text-text-2'
                          }
                        />
                        <span className="flex-1">{it.label}</span>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>

          {/* Footer — compliance band */}
          <div className="border-t border-border-soft px-4 py-3 bg-soft">
            <div className="flex items-center gap-2">
              <ShieldCheck size={12} className="text-navy" />
              <span className="text-[10.5px] font-bold text-text-1 uppercase tracking-wider flex-1">
                HIPAA · 21 CFR Part 11
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                navigate('/compliance');
                setMobileNavOpen(false);
              }}
              className="mt-1.5 text-[11px] text-blue hover:text-navy font-semibold"
            >
              Open compliance hub →
            </button>
          </div>
        </aside>

        {/* Single sidebar toggle button — always mounted, position animates via CSS transition.
            Open:   left=230px -translateX(50%)  bottom=72px  → straddles the sidebar border
            Closed: left=16px  translateX(0)     bottom=20px  → floating at bottom-left corner */}
        <button
          type="button"
          onClick={() => setSidebarOpen((v) => !v)}
          aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          style={{
            left:      sidebarOpen ? '230px' : '16px',
            bottom:    sidebarOpen ? '42px'  : '20px',
            transform: sidebarOpen ? 'translateX(-50%)' : 'translateX(0)',
          }}
          className="group hidden md:flex fixed z-50 w-10 h-10 rounded-full border border-border bg-white shadow-lg items-center justify-center text-text-2 hover:text-navy hover:border-navy transition-all duration-200 ease-in-out"
        >
          {/* Cross-fade the two icons */}
          <PanelLeftClose
            size={18}
            className={cn('absolute transition-opacity duration-200', sidebarOpen ? 'opacity-100' : 'opacity-0')}
          />
          <PanelLeftOpen
            size={18}
            className={cn('absolute transition-opacity duration-200', sidebarOpen ? 'opacity-0' : 'opacity-100')}
          />
          {/* Hover tooltip */}
          <span className="pointer-events-none absolute left-[calc(100%+8px)] top-1/2 -translate-y-1/2 whitespace-nowrap rounded bg-ink-primary px-2 py-1 text-[11px] font-medium text-white opacity-0 shadow group-hover:opacity-100 transition-opacity duration-150">
            {sidebarOpen ? 'Collapse' : 'Expand'}
          </span>
        </button>

        {/* Backdrop for mobile sidebar */}
        {mobileNavOpen && (
          <div
            className="fixed inset-0 z-30 bg-text-1/40 backdrop-blur-sm md:hidden"
            onClick={() => setMobileNavOpen(false)}
            aria-hidden
          />
        )}

        <main className="min-w-0 flex-1 overflow-x-hidden">
          <div className="w-full p-6 md:p-8">{children}</div>
        </main>
      </div>

      {inviteOpen && <InviteModal onClose={() => setInviteOpen(false)} />}

      <ChangePasswordDialog
        open={changePwdOpen}
        onOpenChange={setChangePwdOpen}
        userEmail={user?.email ?? ''}
        userFullName={user?.fullName ?? ''}
      />
      <ResetAuthenticatorDialog open={resetMfaOpen} onOpenChange={setResetMfaOpen} />
    </div>
  );
}
